import { useState } from "react";
import { toast } from "sonner";
import { UserCog, Save, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { formatApiError } from "@/components/auth/VerifyPanel";
import { Field, inputCls } from "@/components/auth/LoginForm";

const KEYS = ["first_name", "last_name", "mobile", "company", "pincode", "city", "state", "country", "purpose"];
const LABEL = { first_name: "First name", last_name: "Last name", mobile: "Mobile (WhatsApp)", company: "Company / Brand", pincode: "Pincode", city: "City", state: "State", country: "Country", purpose: "I am a" };
const PURPOSES = [["creator", "Creator / Influencer"], ["agency", "Agency / Studio"], ["enterprise", "Enterprise / Brand"], ["student", "Student"], ["other", "Other"]];

export function ProfileEditor({ user, onSaved }) {
  const { setUser } = useAuth();
  const { t } = useT();
  const [f, setF] = useState(Object.fromEntries(KEYS.map((k) => [k, user[k] || ""])));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      const r = await api.put("/auth/profile", payload);
      setUser(r.data); toast.success(t("Profile saved")); onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="profile-editor" className="glass rounded-2xl p-5">
      <div className="font-display font-semibold text-sm flex items-center gap-2 mb-4"><UserCog size={15} className="text-cyan-400" /> {t("Profile details")} <span className="text-[10px] font-mono text-slate-500 ml-2">{t(user.auth_provider === "password" ? "Email account" : "Google account")} · {user.email}</span></div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {KEYS.map((k) => (
          <Field key={k} label={t(LABEL[k])}>
            {k === "purpose" ? (
              <select data-testid={`profile-${k}`} value={f[k]} onChange={set(k)} className={inputCls}>{PURPOSES.map(([v, l]) => <option key={v} value={v} className="bg-[#0E1019]">{t(l)}</option>)}</select>
            ) : <input data-testid={`profile-${k}`} value={f[k]} onChange={set(k)} className={inputCls} />}
          </Field>
        ))}
      </div>
      <button data-testid="profile-save" onClick={save} disabled={busy} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-600 to-cyan-600 text-white disabled:opacity-50">{busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} {t("Save profile")}</button>
    </div>
  );
}
